import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { NuovaChiamata } from "@/components/NuovaChiamata";
import { ChevronDown, ChevronUp, Phone, FileText, LifeBuoy, Inbox, Check, X, UserCheck, CalendarClock, Plus } from "lucide-react";

const OPERATIVI = ["doc_agente", "doc_tecnico", "doc_geometra", "manuale"];

const btn = "px-2.5 py-1 rounded-lg border border-[var(--border)] bg-white text-xs font-semibold hover:border-[#FC82ED]/60 transition-colors";
const ESITI = [["risposto", "Risposto"], ["non_risponde", "Non risponde"], ["numero_errato", "Numero errato"], ["non_contattare", "Non contattare"]];
const domani = () => { const d = new Date(); d.setDate(d.getDate() + 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const chiedi = (t) => { const v = window.prompt(t); return v === null ? null : v.trim(); };

function Assegna({ b, onDone, testid, url, body }) {
  const { isSegreteria } = useAuth();
  if (!isSegreteria) return null;
  const altri = b.segretarie.filter((s) => s.id !== b.me);
  const go = async (a) => { try { const r = await api.post(url, { ...body, a }); toast.success(`Assegnato a ${r.data.a.nome}`); onDone(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <span className="inline-flex gap-1.5 items-center flex-wrap">
      <button data-testid={`${testid}-prendo`} className={btn} onClick={() => go(b.me)}><UserCheck size={12} className="inline mr-1" />Prendo io</button>
      {altri.length > 0 && (
        <select data-testid={`${testid}-assegna`} value="" onChange={(e) => e.target.value && go(e.target.value)} className="px-2 py-1 rounded-lg border border-[var(--border)] text-xs bg-white">
          <option value="">Assegna a…</option>
          {altri.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
        </select>
      )}
    </span>
  );
}

function Sezione({ icon: Icon, label, n, children, testid, open: o0 = false, extra }) {
  const [open, setOpen] = useState(o0);
  return (
    <div className="border-t border-[var(--border)] first:border-t-0" data-testid={testid}>
      <button data-testid={`${testid}-toggle`} onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 py-2.5 text-sm font-semibold">
        <Icon size={14} className="text-[var(--muted)]" /> {label}
        <span className="ml-1 px-2 rounded-full bg-[#F7F7F8] border border-[var(--border)] text-xs">{n}</span>{extra}
        <span className="ml-auto text-[var(--muted)]">{open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}</span>
      </button>
      {open && <div className="pb-3 space-y-2">{n === 0 ? <p className="text-xs text-[var(--muted)]">Niente da mostrare.</p> : children}</div>}
    </div>
  );
}

function Riga({ titolo, sub, onOpen, children, testid, danger }) {
  return (
    <div data-testid={testid} className={`p-3 rounded-xl border bg-white ${danger ? "border-red-200" : "border-[var(--border)]"}`}>
      <button onClick={onOpen} className="text-left w-full">
        <div className="text-sm font-semibold">{titolo}</div>
        {sub && <div className="text-xs text-[var(--muted)]">{sub}</div>}
      </button>
      <div className="flex flex-wrap gap-1.5 mt-2 items-center">{children}</div>
    </div>
  );
}

export function DaAssegnare({ b, onDone }) {
  const nav = useNavigate();
  const { isSegreteria } = useAuth();
  const [nuova, setNuova] = useState(false);
  const d = b.da_assegnare;
  return (
    <div className="card-immo p-4 mb-4" data-testid="card-da-assegnare">
      <div className="flex items-center gap-2 mb-1"><Inbox size={16} /><h2 className="font-display font-extrabold">Da assegnare</h2>
        {isSegreteria && <button data-testid="nuova-chiamata-btn" onClick={() => setNuova(true)} className="ml-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#FC82ED] text-black hover:-translate-y-0.5 transition-transform"><Plus size={13} /> Nuova chiamata</button>}
        <span data-testid="da-assegnare-totale" className="ml-auto text-xl font-extrabold">{b.n_da_assegnare}</span></div>
      {nuova && <NuovaChiamata onClose={() => setNuova(false)} onSaved={() => { setNuova(false); onDone(); }} />}
      <Sezione icon={Phone} label="Chiamate" n={d.chiamate.length} testid="da-ass-chiamate">
        {d.chiamate.map((r) => <Riga key={r.id} testid={`da-ass-chiamata-${r.rif_id}`} titolo={`${r.tipo} · ${r.nome}`} sub={`${r.telefono || "—"} · ${r.info} · Agente: ${r.agente}`} onOpen={() => nav(r.link)}>
          <Assegna b={b} onDone={onDone} testid={`da-ass-chiamata-${r.rif_id}`} url="/segreteria/assegna" body={{ tipo: "chiamata", motivo: r.motivo, rif_tipo: r.rif_tipo, rif_id: r.rif_id }} /></Riga>)}
      </Sezione>
      <Sezione icon={FileText} label="Documenti da verificare" n={d.documenti.length} testid="da-ass-documenti">
        {d.documenti.map((r) => <Riga key={r.id + r.doc} testid={`da-ass-doc-${r.id}-${r.doc}`} titolo={r.titolo} sub={`${r.tipo} · Agente: ${r.agente}`} onOpen={() => nav(r.link)}>
          <Assegna b={b} onDone={onDone} testid={`da-ass-doc-${r.id}-${r.doc}`} url="/segreteria/assegna" body={{ tipo: "documento", id: r.id, doc: r.doc }} /></Riga>)}
      </Sezione>
      <Sezione icon={LifeBuoy} label="Richieste agenti (ticket)" n={d.ticket.length} testid="da-ass-ticket">
        {d.ticket.map((t) => <Riga key={t.id} testid={`da-ass-ticket-${t.id}`} titolo={t.titolo} sub={`${t.richiedente_nome || "—"} · aperto il ${(t.created_at || "").slice(0, 10)}`} onOpen={() => nav(`/ticket/${t.id}`)}>
          <Assegna b={b} onDone={onDone} testid={`da-ass-ticket-${t.id}`} url="/segreteria/assegna" body={{ tipo: "ticket", id: t.id }} /></Riga>)}
      </Sezione>
    </div>
  );
}

function AzioniTask({ t, b, onDone, personalizza }) {
  const { isSegreteria } = useAuth();
  const [rim, setRim] = useState(false);
  const [data, setData] = useState("");
  if (!isSegreteria) return null;
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); onDone(); } catch (e) { toast.error(apiError(e)); } };
  const fatto = (nota) => api.put(`/tasks/${t.id}`, { stato: "completato", ...(nota ? { note: nota } : {}) });
  const esito = (e, l) => {
    const n = chiedi(`${l} — nota (facoltativa):`); if (n === null) return;
    run(() => api.post(`/segreteria/chiamata/${t.id}/esito`, { esito: e, nota: n }), e === "non_risponde" ? "Non risponde: richiamo domani" : `Esito registrato: ${l}`);
  };
  return (
    <>
      {t.tipo === "chiamata_seg" && OPERATIVI.includes(t.motivo) && <>
        <button data-testid={`task-${t.id}-fatta`} className={btn} onClick={() => run(() => api.post(`/segreteria/chiamata/${t.id}/esito`, { esito: "fatta" }), "Chiamata fatta")}><Check size={12} className="inline mr-1" />Fatta</button>
        <button data-testid={`task-${t.id}-non_risponde`} className={btn} onClick={() => run(() => api.post(`/segreteria/chiamata/${t.id}/esito`, { esito: "non_risponde" }), "Non risponde: richiamo domani")}>Non risponde</button>
      </>}
      {t.tipo === "chiamata_seg" && !OPERATIVI.includes(t.motivo) && ESITI.map(([e, l]) => <button key={e} data-testid={`task-${t.id}-${e}`} className={btn} onClick={() => esito(e, l)}>{l}</button>)}
      {t.tipo === "documento_seg" && <>
        <button data-testid={`task-${t.id}-approva`} className={btn} onClick={() => run(async () => { await api.post(`/immobili/${t.immobile_id}/checklist/approva`, { doc: t.doc }); await fatto(); }, "Documento approvato")}><Check size={12} className="inline mr-1" />Approva</button>
        <button data-testid={`task-${t.id}-rifiuta`} className={btn} onClick={() => { const m = chiedi("Motivo del rifiuto (obbligatorio):"); if (m === null) return; if (!m) { toast.error("Scrivi il motivo del rifiuto."); return; } run(async () => { await api.post(`/immobili/${t.immobile_id}/checklist/rifiuta`, { doc: t.doc, motivo: m }); await fatto(m); }, "Documento rifiutato"); }}><X size={12} className="inline mr-1" />Rifiuta</button>
      </>}
      {!["chiamata_seg", "documento_seg"].includes(t.tipo) && <>
        <button data-testid={`task-${t.id}-fatto`} className={btn} onClick={() => { const n = chiedi("Fatto — nota (facoltativa):"); if (n !== null) run(() => fatto(n), "Task completato"); }}><Check size={12} className="inline mr-1" />Fatto</button>
        {!rim ? <button data-testid={`task-${t.id}-rimanda`} className={btn} onClick={() => setRim(true)}><CalendarClock size={12} className="inline mr-1" />Rimanda</button> : <>
          <input type="date" data-testid={`task-${t.id}-data`} value={data} min={domani()} onChange={(e) => setData(e.target.value)} className="px-2 py-1 rounded-lg border border-[var(--border)] text-xs" />
          <button data-testid={`task-${t.id}-rimanda-ok`} disabled={!data} className={`${btn} disabled:opacity-40`} onClick={() => run(() => api.put(`/tasks/${t.id}`, { due_date: data }), "Task rimandato")}>Conferma</button></>}
      </>}
      {t.owner_agent_id !== b.me && <button data-testid={`task-${t.id}-prendo`} className={btn} onClick={() => run(() => api.post(`/segreteria/task/${t.id}/assegna`, { a: b.me }), "Task preso in carico")}><UserCheck size={12} className="inline mr-1" />Prendo io</button>}
    </>
  );
}

function TaskRiga({ t, b, onDone, personalizza }) {
  const nav = useNavigate();
  const link = t.immobile_id ? `/immobili/${t.immobile_id}` : t.notizia_id ? `/notizie/${t.notizia_id}` : "/task";
  return (
    <Riga testid={`task-riga-${t.id}`} danger={t.in_ritardo} onOpen={() => nav(link)} titolo={<>{t.tipo === "chiamata_seg" && <Phone size={13} className="inline mr-1" />}{t.titolo}{t.in_ritardo && <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">In ritardo</span>}</>}
      sub={[t.tipo === "chiamata_seg" ? (t.telefono || "telefono non indicato") : t.descrizione, t.tipo === "chiamata_seg" && t.descrizione, t.due_date && `scadenza ${String(t.due_date).slice(0, 10).split("-").reverse().join("/")}`, t.assegnato_da_nome && `assegnato da ${t.assegnato_da_nome}`].filter(Boolean).join(" · ")}>
      {t.tipo === "chiamata_seg" && (
        <div className="w-full">
          {t.telefono && <a data-testid={`task-${t.id}-tel`} href={`tel:${t.telefono}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#B8369F] mb-1"><Phone size={12} /> Chiama {t.telefono}</a>}
          {b.script && t.motivo === "qualita" && <div className="p-2 rounded-lg bg-[#F7F7F8] border border-[var(--border)] text-xs text-slate-600 italic mb-1">"{personalizza(b.script)}"</div>}
        </div>
      )}
      <AzioniTask t={t} b={b} onDone={onDone} personalizza={personalizza} />
    </Riga>
  );
}

function TicketRiga({ t, b, onDone }) {
  const nav = useNavigate();
  const { isSegreteria } = useAuth();
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); onDone(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <Riga testid={`ticket-riga-${t.id}`} onOpen={() => nav(`/ticket/${t.id}`)} titolo={t.titolo} sub={`${t.richiedente_nome || "—"} · ${String(t.stato).replace("_", " ")} · aperto il ${(t.created_at || "").slice(0, 10)}`}>
      {isSegreteria && <>
        <button data-testid={`ticket-${t.id}-rispondi`} className={btn} onClick={() => { const m = chiedi("Risposta al richiedente:"); if (m) run(() => api.post(`/tickets/${t.id}/messaggi`, { testo: m }), "Risposta inviata"); }}>Rispondi</button>
        {t.preso_in_carico_da !== b.me && <button data-testid={`ticket-${t.id}-prendo`} className={btn} onClick={() => run(() => api.post("/segreteria/assegna", { tipo: "ticket", id: t.id, a: b.me }), "Ticket preso in carico")}><UserCheck size={12} className="inline mr-1" />Prendo io</button>}
        <button data-testid={`ticket-${t.id}-chiudi`} className={btn} onClick={() => { if (window.confirm("Chiudere il ticket?")) run(() => api.post(`/tickets/${t.id}/stato`, { stato: "chiuso" }), "Ticket chiuso"); }}>Chiudi</button>
      </>}
    </Riga>
  );
}

export function PersonaCards({ b, onDone, personalizza }) {
  return (
    <div className="grid md:grid-cols-2 gap-4 mb-6" data-testid="card-segretarie">
      {b.persone.map((p) => (
        <div key={p.id} className="card-immo p-4" data-testid={`card-seg-${p.id}`}>
          <div className="flex items-baseline gap-2 mb-1">
            <h2 className="font-display font-extrabold">{p.nome}{p.id === b.me && <span className="ml-1 text-xs font-semibold text-[var(--muted)]">(tu)</span>}</h2>
            <span className="ml-auto text-xs"><b data-testid={`seg-${p.id}-aperti`}>{p.n_aperti}</b> aperti · <b data-testid={`seg-${p.id}-ritardo`} className={p.n_ritardo ? "text-red-600" : ""}>{p.n_ritardo}</b> in ritardo</span>
          </div>
          <Sezione icon={Check} label="Task" n={p.task.length} testid={`seg-${p.id}-task`} open={p.id === b.me}>
            {p.task.map((t) => <TaskRiga key={t.id} t={t} b={b} onDone={onDone} personalizza={personalizza} />)}
          </Sezione>
          <Sezione icon={LifeBuoy} label="Ticket" n={p.ticket.length} testid={`seg-${p.id}-ticket`}>
            {p.ticket.map((t) => <TicketRiga key={t.id} t={t} b={b} onDone={onDone} />)}
          </Sezione>
          <Sezione icon={CalendarClock} label="Storico (7 giorni)" n={p.storico.length} testid={`seg-${p.id}-storico`}>
            {p.storico.map((t) => <div key={t.id} data-testid={`storico-${t.id}`} className="text-xs flex gap-2"><span className="text-[var(--muted)]">{(t.completed_at || "").slice(0, 10).split("-").reverse().join("/")}</span><span className="flex-1">{t.titolo}</span><span>completato da <b>{t.completed_by_name || "—"}</b></span></div>)}
          </Sezione>
        </div>
      ))}
    </div>
  );
}
